import { TextLink } from '@rsa-digital/evo-shared-components/commonTypes/links';
import {
  CustomisableFooterSection,
  IconSectionType,
  RichTextSectionType,
  SimpleSectionType,
} from '@rsa-digital/evo-shared-components/components/Footer/Section';
import React from 'react';
import { processMandatoryImage } from 'helpers/csTypeProcessors';
import { trackFooterClick } from 'helpers/eventTracking';
import { StyledCustomisableFooter } from './styles';
import { IconSectionData, TextLinkData } from '../types';
import useFooterData, { CustomisableFooterData } from '../useFooterData';

const mapTextLink = (textLinkData: TextLinkData): TextLink => ({
  url: textLinkData.url,
  text: textLinkData.text,
  openInNewTab: textLinkData.open_in_new_tab,
  onClick: trackFooterClick(textLinkData.text, textLinkData.url),
});

const mapIconSection = (sectionData: IconSectionData): IconSectionType => ({
  title: sectionData.title,
  linksWithImages: sectionData.links_with_images.map((link) => ({
    image: processMandatoryImage(link.image),
    link: mapTextLink(link.link),
  })),
});

const mapSections = (
  customisableFooter: CustomisableFooterData
): CustomisableFooterSection[] =>
  customisableFooter.sections.map((section) => {
    if (section.simple_section) {
      return {
        title: section.simple_section.title,
        links: section.simple_section.links.map((link) => mapTextLink(link)),
      } as SimpleSectionType;
    }
    if (section.icon_section) {
      return mapIconSection(section.icon_section);
    }
    return {
      title: section.rich_text_section?.title,
      body: section.rich_text_section?.body,
    } as RichTextSectionType;
  });

const CustomisableFooter: React.FC<{ locale: string }> = ({ locale }) => {
  const csCustomisableFooter = useFooterData(locale);

  return (
    <StyledCustomisableFooter
      sections={mapSections(csCustomisableFooter)}
      footerCompliance={csCustomisableFooter.footer_compliance}
      footerNavBar={csCustomisableFooter.footer_nav_bar}
      enableKeyboardNavigation
      data-cy="Customisable Footer"
    />
  );
};

export default React.memo(CustomisableFooter);
